/* eslint-disable */
/**
 * WebsiteEmbed — the copy-paste snippets a brand puts on any website to get
 * the VeriScan verify widget. Everything is hosted by the platform
 * (<API>/embed/v1/…); the brand's site needs no plugin, no files, no build.
 *
 * Two options are shown:
 *   1. Inline widget  — renders directly in the page (matches the site best).
 *   2. Isolated iframe — same widget inside an auto-sizing iframe for sites
 *      whose global CSS fights embedded markup (common on generated sites).
 */
import { useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Badge, FormGroup, Label, Input } from "reactstrap";
import { toast } from "react-toastify";
import { Store } from "StoreContext";
import { mediaUrl } from "config";

const cardStyle = { borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", border: "none", marginBottom: 16 };
const preStyle = {
  background: "#0f172a", color: "#e2e8f0", borderRadius: 10, padding: "14px 16px", fontSize: 12.5,
  lineHeight: 1.55, overflowX: "auto", whiteSpace: "pre", margin: 0, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

function CopyButton({ text }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success("Copied");
      setTimeout(() => setDone(false), 1500);
    } catch (e) {
      toast.error("Copy failed — select the code and copy manually");
    }
  };
  return (
    <button type="button" className="vs-btn-outline-pill" onClick={copy} style={{ fontSize: 12, padding: "4px 12px" }}>
      {done ? "Copied" : "Copy"}
    </button>
  );
}

function Snippet({ title, hint, code, badge }) {
  return (
    <Card style={cardStyle}>
      <CardHeader style={{ background: "#fff", borderBottom: "1px solid #eef0f3", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#1C1C1C" }}>{title}</span>{" "}
          {badge && <Badge color="light" style={{ marginLeft: 8, color: "#362FD8", background: "rgba(54,47,216,0.08)", fontWeight: 600 }}>{badge}</Badge>}
          {hint && <div style={{ color: "#6B7280", fontSize: 13, marginTop: 4 }}>{hint}</div>}
        </div>
        <CopyButton text={code} />
      </CardHeader>
      <CardBody style={{ paddingTop: 12 }}>
        <pre style={preStyle}>{code}</pre>
      </CardBody>
    </Card>
  );
}

const WebsiteEmbed = () => {
  const store = Store();
  const { user } = store || {};
  // Team members act on behalf of the owner brand.
  const brandId = user?.parentBrandId || user?._id || "YOUR_BRAND_ID";
  const brandColor = user?.preferences?.color || "#362FD9";
  const apiOrigin = (mediaUrl || "https://api.getveriscan.com/").replace(/\/$/, "");
  const base = `${apiOrigin}/embed/v1`;

  const [rewardsUrl, setRewardsUrl] = useState("/rewards");
  const [radius, setRadius] = useState("");

  const extra = useMemo(() => {
    let s = "";
    if (rewardsUrl.trim()) s += `\n     data-rewards-url="${rewardsUrl.trim()}"`;
    if (radius.trim()) s += `\n     data-radius="${radius.trim()}"`;
    return s;
  }, [rewardsUrl, radius]);

  const inline = `<link rel="stylesheet" href="${base}/veriscan.css">
<div id="veriscan"
     data-brand-id="${brandId}"
     data-brand-color="${brandColor}"
     data-api-endpoint="${apiOrigin}"${extra}></div>
<script src="${base}/veriscan.js" defer></script>`;

  const iframe = `<script src="${base}/frame.js"
        data-brand-id="${brandId}"
        data-brand-color="${brandColor}"${extra.replace(/\n     /g, "\n        ")}></script>`;

  const react = `// after the page renders, once:
window.VeriScanEmbed.mount(document.getElementById("veriscan"), {
  brandId: "${brandId}", brandColor: "${brandColor}", apiEndpoint: "${apiOrigin}"${rewardsUrl.trim() ? `, rewardsUrl: "${rewardsUrl.trim()}"` : ""}
});`;

  return (
    <div>
      <Card style={cardStyle}>
        <CardBody>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label className="vs-label">Your site's rewards page (optional)</Label>
              <Input value={rewardsUrl} onChange={(e) => setRewardsUrl(e.target.value)} placeholder="/rewards" />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label className="vs-label">Corner radius to match your buttons (optional)</Label>
              <Input value={radius} onChange={(e) => setRadius(e.target.value)} placeholder="e.g. 8px" />
            </FormGroup>
          </div>
          <div style={{ color: "#6B7280", fontSize: 13, marginTop: 12 }}>
            Brand ID <code style={{ color: "#1C1C1C" }}>{brandId}</code> and color <code style={{ color: "#1C1C1C" }}>{brandColor}</code> are filled in.
            The widget inherits your site's font. Point QR codes and NFC tags at your verify page with <code>?h=CODE_HASH</code> and the code verifies automatically.
          </div>
        </CardBody>
      </Card>

      <Snippet
        title="Inline widget"
        badge="Recommended"
        hint="Paste where the verify form should appear. Renders directly in your page and matches it best."
        code={inline}
      />
      <Snippet
        title="Isolated iframe"
        hint="Same widget inside an auto-sizing iframe. Use this if your site's global styles interfere with the inline version. Popups still cover the whole screen on phones."
        code={iframe}
      />
      <Snippet
        title="React / Next / Vue"
        hint="Load veriscan.css and veriscan.js as above, then mount after your element renders."
        code={react}
      />

      <Card style={cardStyle}>
        <CardBody style={{ fontSize: 13, color: "#374151" }}>
          <div style={{ fontWeight: 700, color: "#1C1C1C", marginBottom: 6 }}>Before it goes live</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Your website's domain must be set as your Website Link in Settings so VeriScan accepts requests from it. A "Configuration Error" popup on your site means the domain isn't allowed yet.</li>
            <li>Keep the tag out of sections that use CSS <code>transform</code> or <code>filter</code> (parallax, animated wrappers), or popups will be clipped to that section. The iframe option warns in the browser console when this happens.</li>
            <li>Using WordPress? Install the VeriScan plugin instead and use the <code>[veriscan_code]</code> shortcode.</li>
            <li>Building your own verify page or app? Use the API with a publishable key. See the <a href={`${apiOrigin}/docs`} target="_blank" rel="noreferrer">developer docs</a>.</li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
};

export default WebsiteEmbed;
